import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { isValidEmail, isValidPhone } from "./detailsDialogUtils";

function DetailsInfoDrawer(props) {
  const {
    open,
    entryName,
    images,
    name,
    phone,
    email,
    message,
    sending,
    onNameChange,
    onPhoneChange,
    onEmailChange,
    onMessageChange,
    onSubmit,
  } = props;

  const trimmedName = String(name || "").trim();
  const trimmedEmail = String(email || "").trim();
  const trimmedMessage = String(message || "").trim();
  const trimmedPhone = String(phone || "").trim();
  const previewImage = (Array.isArray(images) ? images : []).find(
    (image) =>
      String(image?.type || "").trim().toLowerCase() === "cover" &&
      (image?.previewUrl || image?.url)
  ) || (Array.isArray(images) ? images : []).find((image) => image?.previewUrl || image?.url);
  const previewSource = previewImage?.previewUrl || previewImage?.url || "";

  let submitBlockReason = "";
  if (!trimmedName) submitBlockReason = "Please enter your name.";
  else if (!trimmedPhone) submitBlockReason = "Please enter your phone number.";
  else if (!isValidPhone(trimmedPhone)) {
    submitBlockReason = "Please input a valid 10 digit phone number.";
  } else if (!trimmedEmail) submitBlockReason = "Please enter your email.";
  else if (!isValidEmail(trimmedEmail))
    submitBlockReason = "Please input a valid email.";
  else if (!trimmedMessage) submitBlockReason = "Please enter a message.";

  const disableSubmit = Boolean(submitBlockReason) || sending;

  if (!open) return null;

  return (
    <Box className="browse-designs-details-info-drawer">
      <Typography
        className="browse-designs-details-fields-title"
        sx={{ mb: 1 }}
      >
        Request Information
      </Typography>
      <Typography variant="body2" sx={{ mb: 1, color: "#4d5c73" }}>
        In Reference to: <strong>{entryName || "N/A"}</strong>
      </Typography>

      {previewSource && (
        <Box
          sx={{
            width: "100%",
            maxHeight: { xs: 140, sm: 180 },
            mb: 1.25,
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={previewSource}
            alt={`${entryName || "Selected design"} preview`}
            sx={{
              width: "100%",
              height: "auto",
              maxHeight: { xs: 140, sm: 180 },
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 1,
          alignItems: "start",
        }}
      >
        <TextField
          label="Name"
          required
          fullWidth
          size="small"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
        />
        <TextField
          label="Phone"
          required
          fullWidth
          size="small"
          value={phone}
          onChange={(event) =>
            onPhoneChange(event.target.value.replace(/\D/g, ""))
          }
          error={phone !== "" && !isValidPhone(phone)}
          helperText={
            phone !== "" && !isValidPhone(phone)
              ? "Please input a valid 10 digit phone number."
              : ""
          }
        />
        <TextField
          sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}
          label="Email"
          required
          fullWidth
          size="small"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          error={email !== "" && !isValidEmail(email)}
          helperText={
            email !== "" && !isValidEmail(email)
              ? "Please input a valid email."
              : ""
          }
        />
        <TextField
          sx={{ gridColumn: "1 / -1" }}
          label="Your Message"
          required
          multiline
          rows={3}
          fullWidth
          size="small"
          value={message}
          onChange={(event) => onMessageChange(event.target.value)}
        />
      </Box>
      <Tooltip
        title={
          disableSubmit
            ? submitBlockReason || "Please finish filling out the form."
            : ""
        }
        disableHoverListener={!disableSubmit}
        disableFocusListener={!disableSubmit}
        disableTouchListener={!disableSubmit}
        disableInteractive={!disableSubmit}
      >
        <span>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#4b4b4b", color: "white", mt: 0.5 }}
            onClick={onSubmit}
            disabled={disableSubmit}
          >
            {sending ? (
              <CircularProgress sx={{ color: "#4b4b4b" }} size={22} />
            ) : (
              "Submit"
            )}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}

export default DetailsInfoDrawer;
